import mqtt from 'mqtt'
import { computed, onBeforeUnmount, reactive, ref } from 'vue'

export function useMqttLightTester (config, tests) {
  let client = null
  let requestSequence = 0
  const pending = new Map()
  const connecting = ref(false)
  const connected = ref(false)
  const connectionError = ref('')
  const deviceStatus = ref('unknown')
  const logs = ref([])
  const clearState = reactive({ sending: false, success: false, skipped: false, error: '', requestId: '', message: '' })

  const log = (type, message) => logs.value.unshift({ type, message, time: new Date().toLocaleTimeString() })
  const nextId = prefix => `${Date.now()}-${++requestSequence}-${prefix}`
  const publish = (payload, target = null) => new Promise(resolve => {
    const id = String(payload.id)
    pending.set(id, target)
    client.publish(config.commandTopic, JSON.stringify(payload), { qos: 0 }, error => {
      if (error) {
        pending.delete(id)
        log('error', `发送失败：${error.message}`)
      } else {
        log('out', `${config.commandTopic}  ${JSON.stringify(payload)}`)
      }
      resolve(error)
    })
  })

  const handleMessage = (topic, raw) => {
    const text = raw.toString()
    log('in', `${topic}  ${text}`)
    let data
    try { data = JSON.parse(text) } catch (error) { return }
    if (topic === config.statusTopic) {
      deviceStatus.value = data.status || 'unknown'
      return
    }
    const id = String(data.id || '')
    if (!pending.has(id)) return
    const target = pending.get(id)
    pending.delete(id)
    if (target === 'clear') {
      if (clearState.requestId !== id) return
      clearState.sending = false
      clearState.success = Boolean(data.result && data.result.success)
      clearState.error = data.error ? `${data.error.code}: ${data.error.message}` : ''
      return
    }
    if (target) {
      target.responseList.push(data)
      if (data.error) target.error = `${data.error.code}: ${data.error.message}`
    }
  }

  const connectMqtt = () => {
    connecting.value = true
    connectionError.value = ''
    try {
      client = mqtt.connect(config.brokerUrl, { reconnectPeriod: 3000, connectTimeout: 10000 })
    } catch (error) {
      connecting.value = false
      connectionError.value = error.message
      return
    }
    client.on('connect', () => {
      connecting.value = false
      connected.value = true
      log('system', `已连接 ${config.brokerUrl}`)
      client.subscribe([config.responseTopic, config.statusTopic], { qos: 0 }, error => {
        if (error) log('error', `订阅失败：${error.message}`)
      })
    })
    client.on('reconnect', () => log('system', '正在重连 MQTT Broker'))
    client.on('close', () => {
      connected.value = false
      deviceStatus.value = 'unknown'
      log('system', 'MQTT 连接已关闭')
    })
    client.on('error', error => {
      connecting.value = false
      connectionError.value = error.message
      log('error', `MQTT 错误：${error.message}`)
    })
    client.on('message', handleMessage)
  }

  const disconnectMqtt = () => {
    if (client) client.end(true)
    client = null
    connected.value = false
    connecting.value = false
  }

  const makePayload = (test, color, id = nextId(test.key)) => {
    const params = { ...test.params }
    if (Object.prototype.hasOwnProperty.call(params, 'strip')) params.strip = Number(config.strip)
    if (Object.prototype.hasOwnProperty.call(params, 'index')) params.index = Number(config.index)
    if (color && Object.prototype.hasOwnProperty.call(params, 'color')) params.color = color
    return { method: test.method, params, id }
  }

  const clearAll = async reason => {
    if (!connected.value || !client) return
    const id = nextId('clear-all')
    clearState.sending = true
    clearState.success = false
    clearState.skipped = false
    clearState.error = ''
    clearState.requestId = id
    clearState.message = reason
    log('system', `准备测试：${reason}，先关闭全部灯带`)
    const error = await publish({ method: 'clear_all', params: {}, id }, 'clear')
    if (error && clearState.requestId === id) {
      clearState.sending = false
      clearState.error = error.message
    }
  }

  const preserveLights = reason => {
    clearState.sending = false
    clearState.success = false
    clearState.skipped = true
    clearState.error = ''
    clearState.requestId = ''
    clearState.message = reason
    log('system', `保留上一项灯效：${reason}`)
  }

  const sendTest = async test => {
    if (!connected.value || !client || test.sending) return
    for (const [id, target] of pending.entries()) {
      if (target === test) pending.delete(id)
    }
    test.sent = true
    test.sending = true
    test.error = ''
    test.responseList = []
    test.confirmed = null
    if (test.sequence) {
      for (const color of test.sequence) await publish(makePayload(test, color), test)
    } else {
      await publish(makePayload(test), test)
    }
    test.sending = false
  }

  const hasSuccessfulResponse = test => test.responseList.length === test.expectedResponses && test.responseList.every(response => response.result && response.result.success === true)
  const payloadPreview = test => JSON.stringify(makePayload(test, test.params.color, `${test.key}-预览`), null, 2)
  const formatJson = value => JSON.stringify(value, null, 2)
  const clearLogs = () => { logs.value = [] }

  const connectionLabel = computed(() => !connected.value ? '未连接' : deviceStatus.value === 'online' ? '设备在线' : deviceStatus.value === 'offline' ? '设备离线' : 'MQTT 已连接')
  const connectionColor = computed(() => !connected.value ? 'grey' : deviceStatus.value === 'online' ? 'success' : deviceStatus.value === 'offline' ? 'error' : 'primary')
  const connectionIcon = computed(() => !connected.value ? 'mdi-lan-disconnect' : deviceStatus.value === 'online' ? 'mdi-check-network' : deviceStatus.value === 'offline' ? 'mdi-alert-network' : 'mdi-lan-connect')
  const completedCount = computed(() => tests.filter(test => test.confirmed !== null).length)
  const passedCount = computed(() => tests.filter(test => test.confirmed === true).length)
  const failedCount = computed(() => tests.filter(test => test.confirmed === false).length)
  const progress = computed(() => tests.length ? completedCount.value / tests.length * 100 : 0)

  onBeforeUnmount(disconnectMqtt)

  return {
    connecting, connected, connectionError, logs, clearState,
    connectionLabel, connectionColor, connectionIcon,
    completedCount, passedCount, failedCount, progress,
    connectMqtt, disconnectMqtt, clearAll, preserveLights, sendTest,
    hasSuccessfulResponse, payloadPreview, formatJson, clearLogs
  }
}
